import React, { useEffect, useRef, useState } from 'react'

import { useTheme } from '@mui/material'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import TooltipWrapper from 'components_new/atoms/TooltipWrapper'

import LoadingSection from 'components_new/molecules/LoadingSection'
import SidebarDialog from 'components_new/molecules/SidebarDialog'

import DataDialog from 'components_new/organisms/DataDialog'
import { useAnalysisDialog } from 'components_new/organisms/DataDialog/useAnalysisDialog'
import AnalysisActivity from 'components_new/organisms/analyses/AnalysisActivity'
import AnalysisObjectSelection from 'components_new/organisms/analyses/AnalysisObjectSelection'
import { TAnalysis } from 'redux/hooks/Analyses'

const PREVIEW_MIN_HEIGHT = 240
const PREVIEW_BOTTOM_GAP = 32

// How much of the document the clipped preview shows before the fade.
const PREVIEW_DOCUMENT_HEIGHT = 1080

interface Version1Props {
  analysis: TAnalysis
}

/**
 * HTML rendered to S3 at generation time. A full document, so the page
 * shows a fading excerpt and opens the whole thing in its own dialog.
 */
const Version1 = ({ analysis }: Version1Props) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const [exploring, setExploring] = useState(false)
  const explore = useAnalysisDialog(analysis)
  const previewHeight = usePreviewHeight()

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        ref={previewHeight.ref}
        sx={{
          bgcolor: 'background.default',
          borderRadius: 1,
          cursor: 'zoom-in',
          height: previewHeight.value,
          overflow: 'hidden',
          position: 'relative',
          width: '100%'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            height: PREVIEW_DOCUMENT_HEIGHT,
            pointerEvents: 'none'
          }}
        >
          <Document disableScroll={true} analysis={analysis} />
        </Box>

        <Box
          sx={{
            alignItems: 'flex-end',
            background: `linear-gradient(to bottom, transparent 40%, ${theme.palette.background.default} 100%)`,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            left: 0,
            pb: 3,
            position: 'absolute',
            right: 0,
            top: 0
          }}
        >
          <Button
            startIcon={<Icon name="ZoomInOutlined" />}
            variant="contained"
          >
            {t('analyses', 'view_evidence')}
          </Button>
        </Box>
      </Box>

      {open ? (
        <SidebarDialog
          actions={
            <Button onClick={() => setOpen(false)} variant="text">
              {t('global', 'close')}
            </Button>
          }
          disableOverflow={true}
          footer={
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
                height: 56,
                px: 3
              }}
            >
              <AnalysisObjectSelection analysis={analysis} />

              <TooltipWrapper title={explore.tooltip}>
                <Button
                  disabled={explore.disabled}
                  onClick={() => setExploring(true)}
                  startIcon={<Icon fontSize="small" name="Explore" />}
                  variant="outlined"
                >
                  {t('global', 'explore')}
                </Button>
              </TooltipWrapper>
            </Box>
          }
          fullHeight={true}
          fullWidth={true}
          maxWidth={false}
          noGutter={true}
          onClose={() => setOpen(false)}
          open={true}
          rightSidebar={{ content: <AnalysisActivity id={analysis.id} /> }}
          title={analysis.title}
        >
          <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
            <Document analysis={analysis} />
          </Box>
        </SidebarDialog>
      ) : null}

      <DataDialog
        {...explore.dialogProps(exploring ? explore.selectedRealEstates : null)}
        depth={1}
        onClose={() => setExploring(false)}
      />
    </>
  )
}

/**
 * Behind a short-lived signed url that carries the selection. Sandboxed:
 * the document is authored per analysis and its scripts do the maths.
 */
const Document = (props: { disableScroll?: boolean; analysis: TAnalysis }) => {
  const { disableScroll, analysis } = props

  const url = analysis.meta?.html

  if (!url) return <LoadingSection />

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <iframe
        sandbox="allow-same-origin allow-scripts"
        scrolling={disableScroll ? 'no' : undefined}
        src={url}
        style={{
          border: 'none',
          display: 'block',
          height: '100%',
          width: '100%'
        }}
        title={analysis.title}
      />
    </Box>
  )
}

/**
 * Whatever height is left below the preview, so the page keeps one
 * scrollbar. Measured every render: everything above it grows as it loads.
 */
function usePreviewHeight() {
  const ref = useRef<HTMLDivElement>(null)
  const [value, setValue] = useState(PREVIEW_MIN_HEIGHT)

  useEffect(() => {
    const update = () => {
      const element = ref.current

      if (!element) return

      const available =
        window.innerHeight -
        element.getBoundingClientRect().top -
        PREVIEW_BOTTOM_GAP

      setValue((current) => {
        const next = Math.max(PREVIEW_MIN_HEIGHT, Math.round(available))

        return next === current ? current : next
      })
    }

    update()

    window.addEventListener('resize', update)

    return () => window.removeEventListener('resize', update)
  })

  return { ref, value }
}

export default Version1
